import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { IActivityApi } from '../api/interfaces/activity-abstract.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import { httpActivitySessionToSession, } from '../helpers/activity.helper';
import { ActivitySessions } from '../models/activity.model';

// 3 minutes
const CACHE_VALIDITY = 180000;

export interface ActivitiesFlowState {
  loading: boolean;
  activities: ActivitySessions;
  total: number | null;
  lastRequestedIndex: number;
}


@Injectable({
  providedIn: 'root'
})
export class ActivitiesFacade {
  private cacheValidityTime = 0;
  private state: ActivitiesFlowState = {
    loading: false,
    activities: [],
    total: null,
    lastRequestedIndex: 0,
  };
  private readonly store = new BehaviorSubject<ActivitiesFlowState>(this.state);
  private readonly state$ = this.store.asObservable();

  public readonly activities$ = this.state$.pipe(map(state => state.activities), distinctUntilChanged());
  public readonly total$ = this.state$.pipe(map(state => state.total), distinctUntilChanged());
  public readonly loading$ = this.state$.pipe(map(state => state.loading), distinctUntilChanged());

  constructor(
    private readonly activityApi: IActivityApi,
    @Inject(NEVER_TOKEN) @Optional() private userId: number,
  ) {}

  getState$(): Observable<ActivitiesFlowState> {
    return this.state$;
  }

  private setState(newState: Partial<ActivitiesFlowState>) {
    this.state = {...this.state, ...newState };
    this.store.next(this.state);
  }

  reset() {
    this.setState({
      loading: false,
      activities: [],
      total: null,
      lastRequestedIndex: 0,
    });
    // TODO -> Unsubcribe get subscriptions
  }

  loadFromStart(size: number) {
    if (this.isCacheValid()) {
      // Check if there is still something to load
      if (size > this.state.lastRequestedIndex) {
        this.loadFromApi(this.state.lastRequestedIndex, size);
      }
    } else {
      this.reset();
      this.loadFromApi(0, size);
    }
  }

  load(start: number, end: number) {
    if (start <= 1) {
      this.loadFromStart(end);
    } else {
      // Check if there is still something to load
      if (end > this.state.lastRequestedIndex) {
        this.loadFromApi(this.state.lastRequestedIndex, end);
      }
    }
  }

  loadFromApi(start: number, end: number) {
    // Do not reload if a page is currently loading
    if (end <= 0 || this.isTotallyLoaded() || this.state.loading) {
      return;
    }

    const { page, pageSize } = this.computePagesAndSizeFromInterval(start, end);

    this.setState({
      loading: true,
      lastRequestedIndex: end
    });

    this.activityApi.getActivities(page, pageSize, this.userId).subscribe({
      next: ({data: httpActivities, meta}) => {
        const activities = httpActivities.map(httpActivitySessionToSession);
        const allActivities = this.mergeNewActivitiesToOldOnes(activities, start);

        this.cacheValidityTime = Date.now() + CACHE_VALIDITY;
        this.setState({
          loading: false,
          activities: allActivities,
          total: meta.total,
        });
        // TODO => Run following data in queue
      },
      error: (err) => {
        console.error(err);
        this.setState({
          loading: false,
        });
      }
    });
  }

  private computePagesAndSizeFromInterval(start: number, end: number): { page: number, pageSize: number } {
    const numberWantedItems = end - start;
    if (start <= 1 || start < numberWantedItems) {
      return {
        page: 1,
        pageSize: end,
      };
    }

    for (let size = numberWantedItems; size < start; ++size) {
      const pageBefore = Math.floor(start / size);
      const newStart = pageBefore * size;
      // end or end-1 ???
      if (newStart + size >= end) {
        return {
          page: pageBefore + 1,
          pageSize: size
        };
      }
    }

    // Should never go here
    return {
      page: 1,
      pageSize: end
    };
  }

  /**
   * It places new activities at the right index in the array
   */
  private mergeNewActivitiesToOldOnes(newActivities: ActivitySessions, offset: number) {
    const mergedActivities = [...this.state.activities];
    newActivities.forEach((activity, index) => mergedActivities[index] = activity);
    return mergedActivities;
  }

  isFirstPageLoaded(): boolean {
    return this.state.total !== null;
  }

  isTotallyLoaded(): boolean {
    return this.state.total !== null && this.state.activities.length >= this.state.total;
  }

  isLoading(): boolean {
    return this.state.loading;
  }

  isCacheValid() {
    return this.cacheValidityTime > Date.now();
  }
}
